import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateIT } from "@/lib/format";
import { Loader2, Search, AlertTriangle } from "lucide-react";
import { useFromState } from "@/lib/navContext";
import { usePersistedFilters } from "@/hooks/usePersistedFilters";
import { useScrollRestore } from "@/hooks/useScrollRestore";

const recallBadge = (r) => ({
  oggi: "bg-amber-100 text-amber-800",
  scaduto: "bg-rose-100 text-rose-700",
  futuro: "bg-emerald-50 text-emerald-700",
  senza: "bg-slate-100 text-slate-500",
}[r] || "bg-slate-100 text-slate-500");

export default function PersonalClientsTab({ preset }) {
  const [data, setData] = useState(null);
  const fromState = useFromState();
  const [f, setF] = usePersistedFilters("pa-clienti", { q: "", portale: "", recall: "", has: "" });
  const { q, portale, recall, has } = f;
  const setQ = (v) => setF((s) => ({ ...s, q: v }));
  const setPortale = (v) => setF((s) => ({ ...s, portale: v }));
  const setRecall = (v) => setF((s) => ({ ...s, recall: v }));
  const setHas = (v) => setF((s) => ({ ...s, has: v }));

  useEffect(() => {
    if (preset) setF((s) => ({ ...s, portale: preset.portale || "", recall: preset.recall || "", has: preset.has || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  useEffect(() => {
    api.get("/portfolio-active/clients").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, []);
  useScrollRestore(!!data);

  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const rows = (data.items || []).filter((c) => {
    if (q && !c.nome.toUpperCase().includes(q.toUpperCase())) return false;
    if (portale === "unassigned" ? c.portale : (portale && (c.portale || "").toLowerCase() !== portale)) return false;
    if (recall && c.recall_stato !== recall) return false;
    if (has === "aperte" && c.pratiche_aperte === 0) return false;
    if (has === "liquidate" && c.pratiche_liquidate === 0) return false;
    return true;
  });

  const sel = "border border-slate-300 rounded-[3px] px-2 py-1.5 text-sm bg-white";
  return (
    <div className="space-y-3" data-testid="pa-clients">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente…" data-testid="pa-clients-search"
            className="pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-[3px] w-52" />
        </div>
        <select value={portale} onChange={(e) => setPortale(e.target.value)} className={sel} data-testid="pa-filter-portale">
          <option value="">Portafoglio: tutti</option><option value="prestiti">Prestiti</option>
          <option value="cessioni">Cessioni</option><option value="unassigned">Da classificare</option>
        </select>
        <select value={recall} onChange={(e) => setRecall(e.target.value)} className={sel} data-testid="pa-filter-recall">
          <option value="">Recall: tutti</option><option value="oggi">Oggi</option>
          <option value="scaduto">Scaduti</option><option value="futuro">Futuri</option><option value="senza">Senza data</option>
        </select>
        <select value={has} onChange={(e) => setHas(e.target.value)} className={sel} data-testid="pa-filter-has">
          <option value="">Pratiche: tutte</option><option value="aperte">Con aperte</option><option value="liquidate">Con liquidate</option>
        </select>
        <span className="text-xs text-slate-500 ml-auto">{rows.length} clienti unici · {rows.reduce((s, c) => s + c.n_pratiche, 0)} pratiche</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-[3px] overflow-x-auto">
        <table className="w-full text-sm min-w-[880px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              {["Cliente", "Portafoglio", "Fonte", "Stato", "Pratiche / prodotti", "Ultima liquidazione", "Ultimo contatto", "Prossimo recall", "Mancano"].map((h) => <th key={h} className="py-2 px-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`pa-client-${c.id}`}>
                <td className="py-2 px-3"><Link to={`/clienti/${c.id}`} state={fromState} className="font-medium text-slate-900 hover:text-[#B7955B]" data-testid={`pa-client-link-${c.id}`}>{c.nome}</Link></td>
                <td className="py-2 px-3 capitalize">{c.portale || <span className="text-amber-700 text-xs">da classificare</span>}</td>
                <td className="py-2 px-3 text-slate-600 max-w-[140px] truncate" title={c.fonte || ""}>{c.fonte || "—"}</td>
                <td className="py-2 px-3 text-xs">{c.stato || "—"}</td>
                <td className="py-2 px-3">
                  <div className="flex flex-wrap gap-1">
                    {c.pratiche.slice(0, 4).map((p) => (
                      <Link key={p.id} to={`/pipeline?mk=${p.monthKey}&focus=${p.id}`} state={fromState}
                        className={`text-[10px] px-1.5 py-0.5 rounded-[3px] border hover:opacity-80 ${
                          (p.stato || "").toUpperCase() === "LIQUIDATA" ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : ["RIFIUTATA", "RITIRATA"].includes((p.stato || "").toUpperCase()) ? "bg-rose-50 border-rose-200 text-rose-700"
                              : "bg-sky-50 border-sky-200 text-sky-700"}`}
                        title={`${p.stato} · ${p.monthKey}`}>{p.prodotto} {p.monthKey}</Link>
                    ))}
                    {c.pratiche.length > 4 && <span className="text-[10px] text-slate-400">+{c.pratiche.length - 4}</span>}
                    {c.n_pratiche === 0 && <span className="text-xs text-slate-400">nessuna</span>}
                  </div>
                </td>
                <td className="py-2 px-3 tabular-nums">{c.ultima_liquidazione ? fmtDateIT(c.ultima_liquidazione) : "—"}</td>
                <td className="py-2 px-3 tabular-nums">{c.ultimo_contatto ? fmtDateIT(c.ultimo_contatto) : "—"}</td>
                <td className="py-2 px-3">
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-[3px] tabular-nums ${recallBadge(c.recall_stato)}`}>
                    {c.prossimo_recall ? fmtDateIT(c.prossimo_recall) : "senza data"}
                  </span>
                </td>
                <td className="py-2 px-3">
                  {c.mancanti.length > 0
                    ? <span className="text-[11px] text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {c.mancanti.join(", ")}</span>
                    : <span className="text-[11px] text-emerald-600">completo</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400 text-sm">Nessun cliente personale nei filtri scelti</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
